import * as d3 from 'd3'
import swal from 'sweetalert'

import Feature from 'ol/Feature'
import { GeoJSON, TopoJSON } from 'ol/format'
import { fromExtent } from 'ol/geom/Polygon'
import { get as projGet, transform as projTransform } from 'ol/proj'
import View from 'ol/View';

import {
  changeAllAr5ResourcesWhenMultipleSelection, justKeepFeasibleAr5Combinations
} from './ar5/combinations'
import { gpsSource } from './gps'
import { OPERATION, setOperation } from './handlers'
import {
  addClickHandlerOn, asTitle, bringUserBackToStart, confirmChanges,
  d3PointFromId, detectFormChanges,
  emphasise, getActiveTab, mitigate, noEmptyAttribute,
  onMouseClick, selectClick,
  selectedFeatureId, selectedFeatureOl, setSelectedFeatureOl,
  showDoneEditingBox, showInfoBoxNoAction, showInfoBoxText,
  showPolygonAttributes, switchTab, toastDestroy, translate
} from './helperFunctions'
import { updateDisabledOptions } from './hardcoded'
import { handleSessionTimeout } from './login'
import { g, lowlightId, resetSVG, resizeMap, updateSVG } from './map/d3Renderer'
import {
  enableEditButtons, rehabilitateDrawing, sketch, updateSnappingGeometry
} from './map/drawing'
import {
  basemap, drawnSource, removableBorders, vectorLayer, updatedVectorLayer
} from './map/layers'
import { getUtm, link, map, toSingleFeature } from './map/tools'
import { getAdjacentFeatures, removeBorder } from './merge'
import {
  askToConfirm, hideEditOptions, moveTabToOperations, showToolbar
} from './navigation'
import {
  loadingGeometry, materializeToast, notifyStatusCode, showMessageBox, simpleToast
} from './notifications'
import {
  addFormInput, createAdvice, getIndexFromValue, populatePreliminaryForm,
  setToday, valueIsHidden, valueIsNotSelectable
} from './populateForms'
import { updateVectorLayersProjection } from './search'
import {
  filterGeometries,
  handleResponseGeometry, loadGeometryToD3, loadGeometryToOpenlayers,
  notifyAccordingToResponseStatus, notifyOutsideResolution, prepareRequest, rest
} from './server'
import {
  codemap, collection, currentDataExtent, currentDataProj, currentLayerId, debug,
  guiConfiguration,
  i18n, layerStyle, locallyAr5, highlighted,
  maxEditResolution, maxZoom, minZoom,
  path,
  setCurrentDataExtent,
  setCurrentGeoType, setCurrentLayer, userCanEditLayers, CURRENT_GEOTYPE,
  setD3Path, setUpdSurfaceWithPolyResAttrEntities,
  updSurfaceWithPolyResAttrEntities
} from './storage'
import {
  blueLineStyle, bluePolygonStyle, invisibleStylePoint, loadStyle,
} from './styles'
import { token } from './tokens'

const ar = ['arealtype', 'treslag', 'skogbonitet', 'grunnforhold'];
const geojson = new GeoJSON();

let XHR, XHRsaving;
// let globalSelectedFeature = null;

const qs = q => document.querySelector(q);
const qsa = q => document.querySelectorAll(q);

const htmlCollectionToArray = (c) => {
  const elements = [];
  for (let i = 0; i < c.length; i++) elements.push(c[i]);
  return elements;
};

const plus = import.meta.env.MODE === 'ar5web_pluss';

export const addAttributesToForm = (a, choices_key) => {
  const form = document.getElementById('info-box-object-attributes-row');
  // Delete all form elements:
  form.innerHTML = '';
  // Add attributes to form:
  a.forEach(attribute => {
    const display = attribute['display_column'];
    if (display === 'yes'
      || (OPERATION === 'edit' && display === 'display_on_update')
      || (OPERATION === 'add' && display === 'display_on_add')
    ) {
      let list = false;
      const attributeChoices = new Array();
      const id = attribute['column_id'];
      const header = attribute['column_header_display'];
      // const name = attribute['column_name'];
      const help_url = attribute['column_help_url'];
      const dataType = attribute['column_datatype'];

      const choices = attribute[choices_key];
      // Check if this attribute is a confined list
      if (choices.length > 0) {
        list = true;
        choices.forEach(choice => {
          attributeChoices.push({
            display_value: choice['column_display'],
            value: choice['column_value']
          });
        });
      } // else { it is a date }
      addFormInput(
        form, list, attributeChoices, id,
        header, help_url, dataType, 'form-update-attribute'
      );
    }
  });
  // Do not show saving button when no changes:
  const i = document.createElement('input');
  i.type = 'checkbox';
  i.id = 'formChanged';
  i.required = true;
  i.hidden = true;
  // i.style.display = 'none';
  if (locallyAr5) form.appendChild(i);
  else {
    form.appendChild(createAdvice('verifisering'));
    const punkt = (CURRENT_GEOTYPE === 'point');
    if (punkt) form.appendChild(createAdvice('flyttPunkt'));
  }
};

const markOption = (s, o) => {
  o.innerHTML += ' <--> [nåværende]';
  s.insertBefore(o, s.firstChild.nextSibling); // second position after Velg...
  s.dataset.previous = 1;
};

const indicateCurrentValue = (e, all) => {
  const p = e.dataset.previous;
  const o = e.options.item(p);
  // console.log(e.id, o.value, o.innerHTML, e.selectedIndex, p);
  o.innerHTML = o.innerHTML.replaceAll(' &lt;--&gt; [nåværende]', '');
  setTimeout(() => {
    if (e.selectedIndex !== Number(p)) markOption(e, o);
    // Other fields will not be triggered if empty, i.e. "Velg...":
    if (all) ar.filter(r => r !== e.id).forEach(r => {
      indicateCurrentValue(document.getElementById(r), false);
    });
  }, all ? 200 : 0);
};

const assignCurrentValuesToForm = () => { // existing geometry
  const original = {}; // used in case of angre button
  const changed = {}; // used to check whether to save

  const sp = selectClick.getFeatures().getArray(); // polygons and points
  const sl = updatedVectorLayer.getSource().getFeatures(); // linestrings
  // The following will work for new features from server as well:
  const selectedFeatures = (sp.length > 0) ? sp : sl;
  // they should be the same:
  // console.log(selectedFeatures[0].getProperties());
  // console.log(selectedFeatureOl.getProperties());
  let selected = selectedFeatures[selectedFeatures.length - 1];
  if (selectClick.getLayer(selected)?.get('id') === 'removableBorders')
    selected = getAdjacentFeatures(selected, 'Polygon', vectorLayer.getSource())[0];
  // Load saved date from selected feature:
  document.querySelectorAll('.datepicker').forEach(dp => {
    // const id = dp.id;
    const id = dp.name;
    if (debug < 16) console.log('load date from layer', id, selected.get(id));
    if (!plus && id.indexOf('verifiseringsdato') < 0) return true; // continue
    const card = dp.parentNode.parentNode.parentNode;
    if (plus && id.indexOf('verifiseringsdato') > -1) {
      const dfd = card.querySelector('input[name="datafangstdato"]');
      dp.min = dfd.value;
    }
    let reverseDate = locallyAr5 ? selected.get(id) :
      selected.get('felles_egenskaper')?.verifiseringsdato;
    if (!reverseDate) reverseDate = dp.value; // today
    else dp.style.color = '';
    // const year = reverseDate.substr(0, 4);
    // const mm = reverseDate.substr(5, 2);
    // const dd = reverseDate.substr(8);
    // const date = dd + '-' + mm + '-' + year;
    dp.value = reverseDate;
    original[id] = reverseDate; // used in case of angre button
    dp.defaultValue = reverseDate;

    if (card.id === 'info-box-object-attributes-row') dp.onchange = () => {
      // console.log('Assigning a date to a existing feature (object)');
      const dateId = dp.name;
      // const value = new Date(dp.value).toLocaleDateString();
      // console.log(dateId, dp.value);
      const [nKey, nValue] = point2nest(dateId, dp.value);
      // const selectedFeatures = updatedVectorLayer.getSource().getFeatures();
      // selectedFeatures[0].setProperties({ [dateId]: dp.value });
      selectedFeatures.forEach(sf => sf.set(
        nKey,
        (dateId.indexOf('.') > -1) ? { ...sf.get(nKey), ...nValue } : nValue
      ));
      changed[nKey] = nValue; // used to check whether to save
      // console.log('onchange', dateId, original[dateId], changed[dateId]);
      if (dp.value === original[nKey]) delete changed[nKey];
      // Trigger emptying klassifiseringsmetode:
      if (locallyAr5 && dp.name === 'verifiseringsdato') {
        dp.dataset.activelyChanged = true;
        // if (!changed.klassifiseringsmetode) { // does not consider empty values
        if (!changed.hasOwnProperty('klassifiseringsmetode')) {
          const km = document.getElementById('klassifiseringsmetode');
          // console.log(km.dataset.activelyChanged);
          if (!km.dataset.activelyChanged) {
            km.value = '';
            km.dispatchEvent(new Event('input'));
            showDoneEditingBox('hide');
          }
        }
      }
      if (noEmptyAttribute()) showDoneEditingBox();
      map.removeInteraction(selectClick); // Force save or cancel after changes.
    };
  });

  // Select attribute values according to last selected feature:
  const htmlC = document.getElementsByClassName('form-update-attribute');
  htmlCollectionToArray(htmlC).forEach(element => {
    const id = element.id;
    let storedValue = selected.get(id)
    let arrayName = id;
    let arrayIndex = -1;
    if (id.indexOf('[') > -1) {
      const s = id.split('[');
      arrayName = s[0];
      arrayIndex = s[1][0];
      storedValue = selected.getProperties()[arrayName][arrayIndex];
    }
    if (debug < 16) console.log('Filling select:', id, storedValue);
    if (storedValue || storedValue === 0) { // 0 --> ikke aktive
      original[id] = storedValue; // used in case of angre button
      element.defaultValue = storedValue.toString();
      if (element.type === 'text') {
        element.value = storedValue;
      } else if (valueIsNotSelectable(storedValue, element)) {
        // add it to the options as disabled and hidden
        const uneditableOption = document.createElement('option');
        uneditableOption.value = storedValue;
        uneditableOption.text = storedValue;
        uneditableOption.disabled = true;
        element.disabled = true;
        if (id === 'anleggstype') {
          if (codemap[id][storedValue]) {
            const t = asTitle(translate(codemap[id][storedValue]));
            uneditableOption.text = t;
          }
          element.disabled = false;
        }
        // uneditableOption.hidden = true;
        element.add(uneditableOption);
      }
      if (valueIsHidden(storedValue, element)) {
        // element.disabled = true; // wrong: be able to select a valid value
        const o = element.querySelector(`option[value=${storedValue}]`);
        if (o) o.disabled = true;
      }
      // Update form based on values on the selected object
      element.value = storedValue;
      if (ar.indexOf(element.id) > -1) {
        // console.log(element.selectedOptions, element.selectedIndex);
        element.dataset.previous = element.selectedIndex;
        // const o = element.selectedOptions.item(0);
        // o.innerHTML += ' <-- nåværende';
      }
    } else {
      // console.log('null value for this attribute');
      if (element.length === 1) {
        // console.log('select element has only one option available');
        updateAttributeValue(element, arrayIndex, selectedFeatures);
        saveSurface(selectedFeatures);
      }
    }

    if (id.indexOf('reinbeitebruker_id2') > -1) {
      const infoBox = element.closest('.info-box');
      const sibling1 = infoBox.getElementsByClassName('reinbeitebruker_id')[0];
      const i = getIndexFromValue(sibling1?.value, element);
      if (i > 0) element.options[i].disabled = true;
    }
    if ((id.indexOf('reinbeitebruker_id3') > -1)) {
      const infoBox = element.closest('.info-box');
      const sibling1 = infoBox.getElementsByClassName('reinbeitebruker_id')[0];
      const sibling2 = infoBox.getElementsByClassName('reinbeitebruker_id2')[0];
      const i = getIndexFromValue(sibling1?.value, element);
      if (i > 0) element.options[i].disabled = true;
      const i2 = getIndexFromValue(sibling2?.value, element);
      if (i2 > 0) element.options[i2].disabled = true;
      if (!storedValue &&
        !infoBox.getElementsByClassName('reinbeitebruker_id2')[0].value
      ) element.parentNode.style.display = 'none';
      else element.parentNode.style.display = 'block';
    }

    if (element.length < 2) return true;
    let previous;

    if (id === 'reinbeitebruker_id') {
      let ga = selected.get('alle_reinbeitebr_id') || '';
      if (ga && ga.constructor === Array) ga = ga[0];
      else if (ga.indexOf('[') > -1) // stringified array
        ga = [...new Set(ga.replace('["', '').replace('"]', '').split('", "'))].join(',');
      const alle = ga || storedValue || '';
      original['alle_reinbeitebr_id'] = alle;
      const a = alle.split(',');
      a.reverse().map(d => qs(`#cb${d}`)).filter(x => x).forEach(x => {
        x.checked = true;
        // console.log('Bring to first place', x.value);
        flereDistrikt.insertBefore(x.parentNode, flereDistrikt.firstChild);
      });
      const cbList = qsa('#flereDistrikt > li > input');
      element.hidden = cbList.length > 1 ? true : false;
      if (cbList.length) flereDistrikt.hidden = !element.hidden;
      if (cbList) cbList.forEach(cb => {
        cb.onchange = () => {
          const a = [];
          const reorderedCbList = qsa('#flereDistrikt > li > input');
          reorderedCbList.forEach(x => { if (x.checked) a.push(x.value); });
          const alle = a.join(',');;
          selected.set('alle_reinbeitebr_id', alle);
          changed['alle_reinbeitebr_id'] = alle;
          previous = element.value;
          element.value = a[0] || '';
          // Trigger update reinbeitebruker_id:
          element.dispatchEvent(new Event('input'));
          // It does not trigger if the value is the same, then:
          selected.set(element.id, element.value);
          changed[element.id] = element.value;
        };
      });
    }

    // Set correct form values
    element.onfocus = () => previous = element.value; // onclick fails on chrome
    // element.onchange = () => { // text fields require blur/tab to trigger
    element.oninput = (evt) => {
      const current = element.value;
      // console.log('oninput', element.id, previous, '-->', current);
      if (ar.indexOf(element.id) > -1) indicateCurrentValue(element, true);
      if (id.indexOf('bruker_id') > -1) {
        updateDisabledOptions(element, previous, current);
      }
      // Show real-time style changes, but do not save to server yet:
      updateAttributeValue(element, arrayIndex, selectedFeatures);
      changed[id] = current; // used to check whether to save
      // console.log('oninput', id, original[id], changed[id]);
      if (current == original[id]) delete changed[id];
      // Chain change to dependent elements:
      if (!locallyAr5) setToday(element.parentNode.parentNode);
      else if (ar.concat(['klassifiseringsmetode']).indexOf(id) > -1) {
        element.dataset.activelyChanged = true;
        if (
          id !== 'klassifiseringsmetode' &&
          !changed.hasOwnProperty('klassifiseringsmetode')
        ) {
          const km = document.getElementById('klassifiseringsmetode');
          // console.log(km.dataset.activelyChanged);
          if (!km.dataset.activelyChanged) {
            km.value = '';
            km.dispatchEvent(new Event('input'));
            showDoneEditingBox('hide');
          }
        }
        // if (!changed.verifiseringsdato) { // does not consider empty values
        if (!changed.hasOwnProperty('verifiseringsdato')) {
          document.getElementsByName('verifiseringsdato').forEach(e => {
            // console.log(e.dataset.activelyChanged);
            if (!e.dataset.activelyChanged) {
              e.value = '';
              e.dispatchEvent(new Event('change'));
              showDoneEditingBox('hide');
            }
          });
        }
      }
      if (locallyAr5) changeAllAr5ResourcesWhenMultipleSelection(changed);
      if (debug < 10) console.log('Show save-button or not?');
      if (noEmptyAttribute()) showDoneEditingBox();
      if (['arealtype', 'treslag', 'skogbonitet'].indexOf(id) > -1) {
        if (!evt.detail) justKeepFeasibleAr5Combinations(true, evt);
      }
      map.removeInteraction(selectClick); // Force save or cancel after changes.
    };
  });

  if (locallyAr5) justKeepFeasibleAr5Combinations(); // existing geometry

  return [original, changed];
};

const defineFormChanges = () => {
  const original = {}; // used in case of regret button
  const changed = {}; // used to check whether to save
  // Date changes:
  const iboa = document.getElementById('info-box-object-attributes-row');
  const dp = iboa.getElementsByClassName('datepicker')[0];
  // Nye flater trenger ikke verifiseringsdato etter at geometrien blir lagret:
  const verifisering = (dp?.name?.indexOf('verifisering') > -1);
  const flate = (CURRENT_GEOTYPE === 'surface');
  if (!locallyAr5 && verifisering && flate) {
    dp.parentNode.parentNode.hidden = true;
    verifiseringAdvice.hidden = true;
  }
  if (dp) {
    dp.onchange = () => { // used when creating new linestring
      if (debug < 13) console.log('datepicker changed');
      const id = dp.name;
      const value = dp.value;
      const object = { [id]: value };
      original[id] = dp.defaultValue;
      changed[id] = value;
      // if a new geometry has just been drawn, id is forstedatafangstdato and
      // verifiseringsdato has to be set to the same date as well (REIN-78)
      if (id.indexOf('forstedatafangstdato') > -1) {
        object[id.replace('forstedatafangst', 'verifiserings')] = value;
      }
      // globalSelectedFeature.setProperties(object);
      selectClick.getFeatures().forEach((f, i) => f.setProperties(object));
      saveSurface(selectClick.getFeatures().getArray());
    };
  } // end if (dp)

  // Outside onchange, the following assignments are wrong (e.g. when splitting
    // a polygon, globalSelectedFeature is still null and updatedVectorLayer has
    // more than just one feature):
  // const selectedFeature = globalSelectedFeature; // or
  // const selectedFeature = updatedVectorLayer.getSource().getFeatures()[0];
  // Set correct form values to feature when changes happen:
  const formFields = document.getElementsByClassName('form-update-attribute');
  const l = formFields.length;
  for (let i = 0; i < l; i++) {
    const attributeField = formFields[i];
    attributeField.oninput = () => {
      if (ar.indexOf(attributeField.id) > -1) indicateCurrentValue(attributeField, true);
      if (noEmptyAttribute()) showDoneEditingBox(); // Show save-button or not?
    };
    const id = attributeField.id;
    attributeField.onchange = (e) => {
      if (debug < 12) console.log('Changing attribute', id);
      const value = attributeField.value; // add "+" in front to convert to number
      original[id] = attributeField.defaultValue;
      changed[id] = value;
      // if (debug < 11) console.log(id, attributeField.defaultValue, value);
      // globalSelectedFeature.setProperties({[id]: value});
      selectClick.getFeatures().forEach(f => f.set(id, value));
      // If user is currently changing the parameter which controls the style,
      // then change the style on the map:
      if (layerStyle && layerStyle.styleAttribute === id) {
        // Change topojson and update svg layer:
        selectClick.getFeatures().forEach((f, i) => {
          const geo_id = f.get('id'); // this is a new id, it does not exist yet
          collection.features.forEach(feature => {
            if (feature.properties.id === geo_id) { // Set new value
              feature.properties[layerStyle.styleAttribute] = value;
            }
          });
        });
        updateSVG();
        // updatedVectorLayer.setStyle([vectorLayer.getStyle()]);
      }
      if (['arealtype', 'treslag', 'skogbonitet'].indexOf(id) > -1) {
        if (!e.detail) justKeepFeasibleAr5Combinations(true, e);
        changeAllAr5ResourcesWhenMultipleSelection(changed);
      }
      // saveSurface(selectedFeatures); // if you want to save at every change
      if (noEmptyAttribute()) {
        if (debug < 10) console.log('Show save-button or not?');
        showDoneEditingBox();
      }
    };
    if (id === 'reinbeitebruker_id') {
      const rid = attributeField.value;
      // console.log(selectClick.getFeatures().item(0).getProperties());
      original['alle_reinbeitebr_id'] = rid;
      const cb = qs(`#cb${rid}`);
      if (cb) cb.checked = true;
      const cbList = qsa('#flereDistrikt > li > input');
      attributeField.hidden = cbList.length > 1 ? true : false;
      if (cbList.length) flereDistrikt.hidden = !attributeField.hidden;
      if (cbList) cbList.forEach(cb => {
        cb.onchange = () => {
          // const alle = cbList.filter(x => x.checked).map(x => x.value);
          const a = [];
          const reorderedCbList = qsa('#flereDistrikt > li > input');
          reorderedCbList.forEach(x => { if (x.checked) a.push(x.value); });
          const alle = a.join(',');
          selectClick.getFeatures().item(0).set('alle_reinbeitebr_id', alle);
          changed['alle_reinbeitebr_id'] = alle;
          attributeField.value = a[0] || '';
          // Trigger update reinbeitebruker_id:
          attributeField.dispatchEvent(new Event('change'));
        };
      });
    }
  };

  return [original, changed];
};

// Used when server sends back new polygon(s):
export const updateFormWithPolygonAttributes = (polygon) => {
  // globalSelectedFeature = polygon;
  const htmlC = document.getElementsByClassName('form-update-attribute');
  htmlCollectionToArray(htmlC).forEach(element => {
    const id = element.id;
    // Update form based on values on the selected object:
    const valueFromServer = polygon.get(id);
    element.value = valueFromServer ?? ''; // Use empty string if null
    if (id === 'reinbeitebruker_id') {
      const cb = qs(`#flereDistrikt #cb${element.value}`);
      if (cb) {
        cb.checked = true;
        flereDistrikt.prepend(cb.parentNode); // Move to first position
      }
    } else if (!plus && id === 'klassifiseringsmetode') element.value = '';
    element.defaultValue = element.value; // Used to compare changes
    if (element.length < 2) { // length of choices, 1 --> Datafangstmetode field
      if (debug < 11) console.log('only one choice');
      element.selectedIndex = 0;
      // save now, because it won't, it does not go through a change event
      // // prepare
      // polygon.setProperties({ [id]: value });
      // save
      let arrayIndex = -1;
      if (id.indexOf('[') > -1) {
        if (debug < 12) console.log(id);
        const s = id.split('[');
        arrayIndex = s[1][0];
      }
      updateAttributeValue(element, arrayIndex, [polygon]);
      saveSurface([polygon]);
      return true;
    } else {
      // console.log('more than one choice');
      if (ar.indexOf(id) > -1) {
        element.dataset.previous = element.selectedIndex;
        indicateCurrentValue(element, false);
      }
    }
  });
  showPolygonAttributes();
  if (locallyAr5) justKeepFeasibleAr5Combinations('new geometry, force attributes choice');
};

function editFeatureAttributes(feature) { // saving points
  const iboa = document.getElementById('info-box-line-attributes');
  const dp = iboa.getElementsByClassName('datepicker')[0];
  dp.onchange = () => {
    if (debug < 11) console.log('datepicker changed');
    const id = dp.name;
    const value = dp.value;
    feature.setProperties({ [id]: value }); // or feature.set(id, value).
    saveSurface(updatedVectorLayer.getSource().getFeatures());
  };

  const htmlC = document.getElementsByClassName('form-update-attribute');
  htmlCollectionToArray(htmlC).forEach(element => {
    // Update form based on values on the selected object:
    element.value = feature.get(element.id);

    if (element.length < 2) return true;
    // Set correct form values
    element.onchange = () => {
      feature.setProperties({ [element.id]: element.value });
      // If user is currently changing the parameter which controls the style
      // Then change the style on the map
      if (layerStyle.styleAttribute === element.id) {
        // Change topojson and update svg layer
        const id = feature.getProperties().id;
        collection.features.forEach(d3feature => {
          if (d3feature.properties.id === id) {
            // Set new value
            d3feature.properties[layerStyle.styleAttribute] = element.value;
            d3feature.properties['newFeature'] = false;
            updateSVG();
          }
        });
      }
      saveSurface(updatedVectorLayer.getSource().getFeatures());
    };
  });

  if (CURRENT_GEOTYPE !== 'point') showPolygonAttributes();
}

function name2id(name) {
  return name.split(' ').join('');
}

let layersPerParent = {};
export let id2name = {};

export function createMenuItem(idx, value) {
  const layer_id = value.layer_id;
  const layer_name = value.layer_name;
  const layer_type = value.layer_type;
  const parent_name = value.parentMenuItem;
  // console.log(layer_id, layer_name, parent_name);
  id2name[layer_id] = {parent_name, layer_name}; // need it in qualityCheck table
  if (parent_name) {
    const parent_id = name2id(parent_name);
    if (!document.getElementById(parent_id)) {
      layersPerParent[parent_id] = [value];
      $("#layer-button-div").append(`
        <button id="`+parent_id+`" class="dropdown-button parent-button">
          <b>`+parent_name+`</b>
        </button>
        <!-- Dropdown Structure -->
        <ul id='dd-`+parent_id+`' class='rein-dropdown' hidden>
          <li>
            <button id="`+layer_id+`" class="layer-button `+layer_type+`">` +
              layer_name +
            `</button>
          </li>
        </ul>
      `);
    } else {
      layersPerParent[parent_id].push(value);
      const l = layersPerParent[parent_id].length;
      $('#dd-'+parent_id).append(`
        <li>
          <button id="`+layer_id+`" class="layer-button `+layer_type+`">` +
            layer_name +
          `</button>
        </li>
      `);
    }
  } else {
    layersPerParent.root = [value];
    $("#layer-button-div").append(`
      <button id="`+layer_id+`" class="layer-button root `+layer_type+`">` +
        layer_name +
      `</button>
      <br>
    `);
  }
  return;
}

// To sort an array of obj by obj.key: array.sort(byKey('key'));
export function byKey(k) {
    return function (a, b) {
        // return ((a[k] === b[k]) ? 0 : ((a[k] > b[k]) ? 1 : -1));
        return parseInt(a[k], 10) - parseInt(b[k], 10);
    };
};

// To sort an array of obj by 'sortKey': array.sort(bySortKey);
function bySortKey (a, b) {
  return parseInt(a.sortKey, 10) - parseInt(b.sortKey, 10);
};

export function checkExtent(e) {
  // Reindrift:
  if ( (e[0] > 9.59) && (e[2] < 31.44) ) return false;
  if ( (e[1] > 61.81) && (e[3] < 71.30) ) return false;
  // // Norge:
  // if ( (e[0] > 4.4) && (e[2] < 31.2) ) return false;
  // if ( (e[1] > 57.9) && (e[3] < 71.3) ) return false;
  return true;
}

export function layerButtonClicked(button) {
  const name = button.innerHTML;
  const rid = button.parentNode.parentNode.id.replace('dd-', '');
  const pre = button.classList.contains('root') ? '' : qs(`#${rid} > b`).innerHTML;
  document.getElementById('tabSave').innerHTML = 'Oppdatere ' + name;
  // Hide layer box
  $('#info-box-choose-layer').hide();
  // Move tab one to the right
  switchTab('Home', 'Operations');
  const ibcett = document.getElementById('info-box-choose-edit-type-title');
  if (ibcett) ibcett.innerHTML = 'Hva ønsker du å gjøre med ' + name + '?';

  setCurrentLayer(button.id, pre || name, pre ? '' : name);
  if (button.classList.contains('point')) setCurrentGeoType('point');
  else if (button.classList.contains('line')) setCurrentGeoType('line');
  else if (button.classList.contains('surface')) setCurrentGeoType('surface');

  prepareEditLayer();

  //  Update texts on buttons.
  //  Get texts from config/locales/nb.json

  // Get buttons
  const gc = document.getElementById('geometry-change');
  const gcbg = document.getElementById('geometry-change-by-gps');
  const gca = document.getElementById('geometry-change-attributes');
  emphasise('geometry-change');
  emphasise('geometry-change-attributes');
  if (userCanEditLayers) {
    // emphasise('geometry-change-by-gps');
  } else {
    mitigate('geometry-change');
    // mitigate('geometry-change-by-gps');
    mitigate('geometry-change-attributes');
  }

  // Create string based on button id
  const geometryChangeButtonText = gc.id + '-button-text';
  const geometryChangeAttributesButtonText = gca.id + '-button-text';

  const vocabulary = i18n[CURRENT_GEOTYPE];
  if (!locallyAr5) gc.innerHTML = vocabulary[geometryChangeButtonText];
  if (!locallyAr5) gca.innerHTML = vocabulary[geometryChangeAttributesButtonText];

  // Create string based on button id
  const geometryChangeHelpText = gc.id + '-help-text';
  const geometryChangeByGPSHelpText = gcbg.id + '-help-text';
  const geometryChangeAttributesHelpText = gca.id + '-help-text';

  // Change help texts
  const s = 'område'; // search
  const r = locallyAr5 ? 'AR5-flate' : s; // replace
  gc.dataset.tooltip = vocabulary[geometryChangeHelpText].replace(s, r);
  gcbg.dataset.tooltip = vocabulary[geometryChangeByGPSHelpText].replace(s, r);
  const reinTooltip = vocabulary[geometryChangeAttributesHelpText];
  const ar5Tooltip = vocabulary['geometry-change-attributes-help-text-ar5'];
  gca.dataset.tooltip = locallyAr5 ? ar5Tooltip : reinTooltip;
  if (!userCanEditLayers) {
    const qct = 'Kvalitetssikring pågår, det er ikke lov til å redigere lag';
    gc.dataset.tooltip = qct;
    // gcbg.dataset.tooltip = qct;
    gca.dataset.tooltip = qct;
  }

  // Get json style for this layer and set global layerStyle variable
  // If surface set add patterns to SVG element
  loadStyle(currentLayerId);

  // Show utenforWMS when årstidsbeite is chosen:
  const u = document.getElementById('utenforWMS');
  u.checked = (name.slice(-5) === 'beite');
  $(u).trigger('change');
}

function prepareEditLayer(zoomToFeatures) {
  if (debug < 16) console.log('prepareEditLayer --> prepare_editlayer_request');
  // reset OPERATION before we start on a new edit/insert operation
  setOperation('');

  // Set zoomToFeatures as default true
  zoomToFeatures = typeof zoomToFeatures !== 'undefined' ? zoomToFeatures : true;

  if (map.getView().getResolution() > maxEditResolution) { // do not even ask server
    map.un('singleclick', onMouseClick); // avoid postgis layer outside resolution
    notifyOutsideResolution();
    return;
  }

  loadingGeometry(true);

  fetch(rest + '/prepare_editlayer_request' + token, {
    method: 'post',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(prepareRequest())
  }).then(response => {
    if (response.ok) return response.json();
    else  {
      response.getResponseHeader = (h) => response.headers.get(h);
      handleSessionTimeout(response);
    }
  }).then(data => {
    if (!data || tabHome.classList.contains('active')) return;
    notifyAccordingToResponseStatus(data.status);
    const cm = data.current_map;
    const str = (typeof cm === 'string' || cm instanceof String);
    const json = str ? JSON.parse(cm) : cm;
    const snapNodes = locallyAr5 || filterGeometries(structuredClone(json), 'point');
    filterGeometries(json, CURRENT_GEOTYPE);
    handleResponseGeometry(json);
    if (data.current_map) {
      loadGeometryToOpenlayers(json, vectorLayer);
      loadGeometryToD3();
      if (locallyAr5) updateSnappingGeometry(data.snapToNodes);
      else snapNodes.filter(g => g.properties?.role === 'snap-nodes').forEach(
        g => updateSnappingGeometry(g)
      );
      loadGeometryToOpenlayers(data.current_map_remove_border, removableBorders);
      // Show feature properties:
      map.on('singleclick', onMouseClick);
    }
    console.log('populate', data.prepareEditLayerResAttrEntities);
    // Populate form for preliminary values to send to backend, before saving
    populatePreliminaryForm(
      document.getElementById('info-box-drawnline-attributes-row'),
      data.prepareEditLayerResAttrEntities
    );
  }).catch(e => console.log(e)).finally(() => loadingGeometry(false));
}

function almostSameExtent() {
  const e = map.getView().calculateExtent(map.getSize());
  if (currentDataExtent.length === 0) { return false; }
  const lastW = currentDataExtent[2] - currentDataExtent[0];
  const lastH = currentDataExtent[3] - currentDataExtent[1];
  const newW = e[2] - e[0];
  const newH = e[3] - e[1];
  const wChange = Math.abs(newW - lastW);
  const hChange = Math.abs(newH - lastH);
  const wPercentage = wChange * 100 / lastW;
  const hPercentage = hChange * 100 / lastH;
  const a = e[0] - currentDataExtent[0];
  const b = e[1] - currentDataExtent[1];
  const displacement = Math.sqrt( a*a + b*b );
  const movePercentage = displacement * 100 / ((lastW + lastH) / 2);
  if ( (wPercentage < 5) && (hPercentage < 5) ) {
    if (movePercentage < 5) { return true; } // avoid panning (same extent)
  }
  return false;
}

export function updateLayerData() {
  if (debug < 16) console.log('updateLayerData --> prepare_editlayer_request');
  if (map.getView().getResolution() > maxEditResolution) { // do not even ask server
    map.un('singleclick', onMouseClick); // avoid postgis layer outside resolution
    notifyOutsideResolution();
    return;
  }

  // need it when calling mapResize to avoid loosing red boundary highlight
  if (almostSameExtent()) {
    // console.log('Almost same extent, suspend updateLayerData');
    resetSVG('complete'); // map panning without sending request
    return;
  }

  if (XHR) XHR.abort(); // avoid hanging requests on map panning/zooming
  loadingGeometry(true);
  XHR = $.ajax({
    type: 'POST',
    url: rest + '/prepare_editlayer_request' + token,
    data: JSON.stringify(prepareRequest()),
    success: data => {
      console.log(getActiveTab());
      // came back to first tab before request succeeded:
      if (getActiveTab() === 1) return;

      notifyAccordingToResponseStatus(data.status);
      const cm = data.current_map;
      const str = (typeof cm === 'string' || cm instanceof String);
      const json = str ? JSON.parse(cm) : cm;
      const snapNodes = locallyAr5 || filterGeometries(structuredClone(json), 'point');
      filterGeometries(json, CURRENT_GEOTYPE);
      handleResponseGeometry(json);

      // Set
      updatedVectorLayer.getSource().getFeatures().forEach(selectedFeature => {
        // Get id of the selected feature
        const selectedId = selectedFeature.get('id');
        // Check if this selected feature is new or
        // if properties is already set on object
        const newFeature = !selectedFeature.get('reindriftsanleggstype') &&
                           !selectedFeature.get('anleggstype');
        // Go through all features that D3 will redraw and reset properties
        // so that D3 will draw them correct.
        if (collection?.features) collection.features.forEach(feature => {
          if (feature.properties.id === selectedId) {
            // Set properties so D3 will render this feature correctly
            feature.properties.selected = true;
            if (newFeature) {
              feature.properties.newFeature = true;
            } else {
              feature.properties.newFeature = false;
            }
          }
        });
      });

      // Only once, when the form is still empty:
      if (qs('#info-box-drawnline-attributes-row').children.length === 0) {
        console.log('populate 2');
        // Populate form for preliminary values to send to backend before saving
        populatePreliminaryForm(
          document.getElementById('info-box-drawnline-attributes-row'),
          data.prepareEditLayerResAttrEntities
        );
      } else console.log('Already populated');

      if (!data.current_map) return;
      loadGeometryToOpenlayers(json, vectorLayer);
      loadGeometryToD3();
      if (locallyAr5) updateSnappingGeometry(data.snapToNodes);
      else snapNodes.filter(g => g.properties?.role === 'snap-nodes').forEach(
        g => updateSnappingGeometry(g)
      );
      loadGeometryToOpenlayers(data.current_map_remove_border, removableBorders);

      // Show feature properties:
      map.on('singleclick', onMouseClick);

      resizeMap();
    },
    error: handleSessionTimeout,
    complete: () => loadingGeometry(false),
    contentType: 'application/json',
    dataType: 'json'
  });
}

let nyAr5FlateMedUendretEgenskaper = false, originals = {};
regretButton.onclick = () => {
  const selectedPointD3 = d3PointFromId(selectedFeatureId);
  if (selectedPointD3) selectedPointD3.datum().properties.selected = false;
  if (selectedPointD3) selectedPointD3.style('fill', 'black');
  lowlightId(highlighted);
  updatedVectorLayer.getSource().getFeatures().forEach((sf, i) => {
    sf.setProperties(originals[sf.get('id')]);
  });
  moveTabToOperations('force, without warning about missing values')
  bringUserBackToStart();
  map.getView().setMinZoom(minZoom);
  hideEditOptions();
  showToolbar('none');
  if (locallyAr5) {
    if (nyAr5FlateMedUendretEgenskaper) {
      // Show message when a new ar5 area has been drawn:
      showMessageBox('cancelProps', '#E94E0F');
      nyAr5FlateMedUendretEgenskaper = false;
    } else if (detectFormChanges())
      simpleToast('Endring er IKKE blitt lagret', 'error');
  }
};

const anyChanges = (changed) => new Promise((resolve, reject) => {
  if (Object.keys(changed).length === 0) resolve(false);
  else {
    const info = document.getElementById('informasjon');
    if (
      locallyAr5 && info.value &&
      changed.informasjon === undefined // informasjon is unchanged
    ) {
      // console.log('opphev felt og foreslå å tømme');
      // Remember style, in case there is one:
      const originalBorder = info.style.border;
      const originalTransform = info.style.transform;
      info.style.border = 'thick solid red';
      info.style.transform = 'translate(50%) scale(2)';
      askToConfirm(
        'Skal du ikke slette informasjonen på flaten?'
      ).then(confirmed => {
        // console.log('svar er', confirmed ? 'ja' : 'nei');
        const s = updatedVectorLayer.getSource().getFeatures();
        const f = s[s.length - 1]; // last selected feature
        if (confirmed) f.set('informasjon', '');
        info.style.border = originalBorder;
        info.style.transform = originalTransform;
        resolve(true);
      });
    } else resolve(true); // reindrift
  }
});

const dateIsInvalid = () => {
  const dps = document.getElementsByClassName('datepicker');
  const l = dps.length;
  for (let i = 0; i < l; i++) {
    const dp = dps[i];
    // const isVisible = dp.offsetParent !== null;
    const isVisible = dp.offsetWidth > 0 || dp.offsetHeight > 0;
    if (isVisible && !dp.checkValidity()) {
      swal({
        title: 'Ugyldig dato',
        text: dp.validationMessage,
        type: 'warning',
        confirmButtonText: 'Ok'
      });
      return true;
    }
  }
  return false;
};

export function getSurfaceAttributes(id) {
  if (debug < 10) console.log('getSurfaceAttributes with OPERATION', OPERATION);
  $.ajax({
    type: 'POST',
    url: rest + '/get_surface_attribute' + token,
    data: JSON.stringify({
      layer_id: currentLayerId,
      response_srid: map.getView().getProjection().getCode().split(':')[1],
      row_id: id
    }),
    success: data => {
      const cm = data['current_map'];
      if (cm) {
        const str = (typeof cm === 'string' || cm instanceof String);
        const json = str ? JSON.parse(cm) : cm;
        const gg = json?.objects?.collection?.geometries || json?.features;
        // console.log(gg[0].properties.editable, selectedFeatureId);
        if (!selectedFeatureOl) setSelectedFeatureOl(selectClick.getFeatures().item(0));
        const temp = selectClick.getFeatures().item(0).get('editable');
        if (gg.length > 0 && (!temp && !gg[0].properties.editable)) {
          moveTabToOperations();
          notifyAccordingToResponseStatus(1100);
          return;
        }
        if (!locallyAr5) selectedFeatureOl.setProperties(gg[0].properties);
      }

      // Save list of attributes for later use
      setUpdSurfaceWithPolyResAttrEntities(data.getSurfaceAttributeResAttrEntities);

      addAttributesToForm(
        data['getSurfaceAttributeResAttrEntities'],
        'getSurfaceAttributeResAttrValuesEntities'
      );

      // original is used in case of angre button.
      // changed is used to check whether to save.
      const [original, changed] = assignCurrentValuesToForm();
      // original is not common to every selected features like changed:
      originals = {};
      updatedVectorLayer.getSource().getFeatures().forEach((sf, i) => {
        originals[sf.get('id')] = sf.getProperties();
      });

      if (debug < 12) console.log('Onclick to change existing feature');
      doneButton.onclick = (e) => {
        if (debug < 16) console.log('Changing existing feature');
        if (dateIsInvalid()) return;
        // console.log(Object.keys(changed));
        const c = changed;
        const a = ['arealtype', 'grunnforhold', 'skogbonitet', 'treslag'];
        const ar5PropertiesChanged = c[a[0]] || c[a[1]] || c[a[2]] || c[a[3]];
        // datafangstdato := verifiseringsdato only if ar5PropertiesChanged:
        if (ar5PropertiesChanged) assignSelectedDate();
        anyChanges(changed).then(yes => {
          if (yes && locallyAr5) confirmChanges(original, changed);
          else {
            if (!locallyAr5) {
              const uff = updatedVectorLayer.getSource().getFeatures();
              const dff = drawnSource.getFeatures();
              // console.log(uff, dff);
              const movedPoint = dff.length > 0; // && CURRENT_GEOTYPE === 'point'
              if (movedPoint) {
                const mp = dff[0];
                // dff har gamle egenskaper, bare behold ny geometri og id:
                Object.keys(mp.getProperties()).filter(
                  k => ['geometry', 'id'].indexOf(k) < 0
                ).forEach(k => mp.set(k, undefined, true));
                saveMovedPoint(mp);
              }
              if (yes) saveSurface(uff, null, changed);
            }
            bringUserBackToStart();
            map.getView().setMinZoom(minZoom);
          }
        });
      };

      // Move tab one to the right
      switchTab('DrawPick', 'Save');

      showPolygonAttributes();
    },
    error: handleSessionTimeout,
    contentType: 'application/json',
    dataType: 'json'
  });

  // // Remove interaction so user cannot click to a new object
  // map.removeInteraction(selectClick);
}

const assignSelectedDate = (newlySplitFeatures) => {
  const ibdar = document.getElementById('info-box-drawnline-attributes-row');
  const dp = ibdar.getElementsByClassName('datepicker')[0] || {};
  const dateId = dp.name;
  // console.log(dateId, dp.value);
  selectClick.getFeatures().forEach(sf => {
    if (!newlySplitFeatures) { // changing markslag to existing features
      // update datafangstdato with chosen date, that is verifiseringsdato,
      // iboar is where verifiseringsdato is and ibdar is datafangstdato:
      const iboar = document.getElementById('info-box-object-attributes-row');
      const dp_v = iboar.getElementsByClassName('datepicker')[0] || {};
      // console.log(dp_v.name, dp_v.value);
      if (debug < 14) console.log('now', sf.get(dateId), 'assign', dp_v.value);
      sf.set(dateId, dp_v.value);
    } else if (sf.get(dateId) !== dp.value) { // case of new split feature
      if (debug < 14) console.log('now', sf.get(dateId), 'assign', dp.value);
      // console.log('split feature, assign');
      // Save verifiseringsdato and forstedatafangstdato to geometry:
      sf.set(dateId, dp.value);
      const v = 'verifiserings';
      const df = 'datafangst';
      const dateId_v = dateId.replace('forste' + df, v).replace(df, v);
      sf.set(dateId_v, dp.value);
      // console.log(dateId, dateId_v);
      // changed[dateId] = dp.value; // used to show the change to the user
    } else { // new independent drawn feature, dates are already right
      // console.log('indipendent feature');
    }
  });
};

export const point2nest = (key, value) => {
  const keys = key.replace('fellesegenskaper', 'felles_egenskaper').split('.').reverse();
  const nest = [keys[0], value];
  keys.slice(1).forEach(k => {
    nest[1] = { [nest[0]]: nest[1] };
    nest[0] = k;
  });
  return nest;
};

export function saveNewLine(lineFeature, kindOfLine) {
  if (debug < 16) console.log('saveNewLine with OPERATION', OPERATION);

  const updateRequest = {
    layer_id: currentLayerId,
    response_srid: map.getView().getProjection().getCode().split(':')[1]
  };

  // Add drawn line geometry to post
  if (!lineFeature) lineFeature = toSingleFeature(drawnSource.getFeatures());

  const gps = (kindOfLine === 'gps');
  if (gps) lineFeature = toSingleFeature(gpsSource.getFeatures());

  // Set chosen properties on line:
  const css = '#info-box-drawnline-attributes-row .form-input-element';
  const selectItems = document.querySelectorAll(css + ' select');
  const inputItems = document.querySelectorAll(css + ' input:not([type="hidden"])');
  const sil = selectItems.length;
  const iil = inputItems.length;
  for (let index = 0; index < sil; index++) {
    const select = selectItems[index];
    if (debug < 16) console.log('Select item:', select);
    const key = select.id.toString();
    const value = select.value;
    lineFeature.set(key, value);
  };
  for (let index = 0; index < iil; index++) {
    const input = inputItems[index];
    if (debug < 16) console.log('Input:', input.id, input.name, input.value);
    const date = (input.type === 'date');
    const key = (date) ? input.name : input.id;
    const value = input.value;
    lineFeature.set(key, value);
  };

  // REIN-57 (new geometry, hence verifiserings := forstedatafangst):
  const felles = locallyAr5 ? '' : 'fellesegenskaper.' ;
  const fd = lineFeature.get(felles + 'forstedatafangstdato');
  const vd = lineFeature.get(felles + 'verifiseringsdato');
  if (fd && !vd) lineFeature.set(felles + 'verifiseringsdato', fd);

  // Set datafangstmetode:
  const key = locallyAr5 ? 'datafangstmetode' : felles + 'kvalitet.maalemetode';
  let value = locallyAr5 ? 'dig' : 82;
  if (kindOfLine === 'gps') {
    if (debug < 1) console.log('Saved as GPS!');
    value = 92;
  }
  lineFeature.set(key, value);

  const nested = {};
  Object.entries(lineFeature.getProperties()).forEach(([k, v]) => {
    if (k.indexOf('.') > -1) {
      const [nKey, nValue] = point2nest(k, v);
      lineFeature.unset(k);
      if (nested[nKey]) Object.assign(nested[nKey], nValue)
      else nested[nKey] = nValue;
    }
  });
  Object.entries(nested).forEach(([k, v]) => lineFeature.set(k, v));
  const drawnLine = geojson.writeFeatureObject(lineFeature);

  // Add crs
  const epsg = map.getView().getProjection().getCode();
  drawnLine.geometry.crs = { type: 'name', properties: { name: epsg } };

  // Reindrift API
  const cloned = { ...drawnLine };
  if (CURRENT_GEOTYPE !== 'point') {
    const n = { new: cloned.properties };
    cloned.properties = (CURRENT_GEOTYPE === 'surface') ? { Surface: n } : n;
  }
  if (!locallyAr5) updateRequest.feature_properties = JSON.stringify(cloned);

  // AR5web API
  const jsonS = JSON.stringify(drawnLine).replace(/"null"/g, "null");
  updateRequest.map_update = '"' + jsonS + '"';
  if (debug < 6) console.log('sent', drawnLine.properties);
  if (debug < 6) console.log('newLine sending:', updateRequest);
  // console.log('Saving to server! (Start loading animation)');
  document.getElementById('bar').style.display = 'block';
  document.getElementById('twdStep2').hidden = true;
  document.getElementById('twdStep3').hidden = false;
  const api = locallyAr5 ? '/update_surfacelayer_request' : '/draw';
  $.ajax({ // A new feature has been drawn, send it to back-end
    type: 'POST',
    url: rest + api + token,
    data: JSON.stringify(updateRequest),
    success: data => {
      if (debug < 16) console.log('new geometry received:', data);

      if (!locallyAr5) notifyAccordingToResponseStatus(data.status);
      else notifyStatusCode(OPERATION, data.status, data.statusErrorServerMsg);
      // Check for error
      if (data['status'] !== 1) {
        // Move to previous tab
        // moveTabToOperations();
        // ...or stay and keep drawing on rejected geometry:
        rehabilitateDrawing();
        showInfoBoxText();
      } else {
        // Remove old SVG objects
        g.selectAll('path').remove();
        g.selectAll('text').remove();

        // Removed drawn features
        drawnSource.clear();
        sketch.getGeometry().setCoordinates([]); // used to skip ar5 check

        // Save list of attributes for later use
        setUpdSurfaceWithPolyResAttrEntities(data.updSurfaceWithPolyResAttrEntities);

        const jsonFormat = locallyAr5 ? TopoJSON : GeoJSON;
        const features = new jsonFormat({
          dataProjection: projGet('EPSG:4326')
        }).readFeatures(data['changed_area']);

        if (debug < 16) {
          console.log('received', features.length, 'feature(s):');
          features.forEach(
            (f, i) => console.log('  ', i, ' --> ', f.getProperties())
          );
        }
        if (!locallyAr5) features.forEach(f => {
          const rid = f.get('reinbeitebruker_id');
          const aid = f.get('alle_reinbeitebr_id');
          if (aid === '') f.set('alle_reinbeitebr_id', rid);
        });
        updatedVectorLayer.getSource().addFeatures(features);

        // Update collection with new polygons
        setCurrentDataExtent([]); // force reload by skiping almostSameExtent()
        updateLayerData();

        if (!locallyAr5) setOperation('edit'); // to show display_on_update attributes
        addAttributesToForm(
          updSurfaceWithPolyResAttrEntities,
          'updSurfaceWithPolyResAttrValuesEntities'
        );
        // Feature values will be assigned to form attributes when user
        // selects a new feature that came from backend:
        // within addClickHandlerOn, by using updateFormWithPolygonAttributes.

        // Onchange events can be defined here to be able to update "changed":
        const [original, changed] = defineFormChanges();
        // original is used in case of regret button
        // changed is used to check whether to save

        // Make vector layer clickable:
        addClickHandlerOn(updatedVectorLayer);

        const saveAllSplitFeatures = (selectedSplit) => {
          if (locallyAr5) {
            // Those which are not selected will have default synbarhet
            const selectedSplitId = selectedSplit[0].get('id');
            updatedVectorLayer.getSource().getFeatures().forEach((f, i) => {
              if (f.get('id') === selectedSplitId) return;
              // console.log('Saving', f.get('id'));
              saveSurface([f]);
            });
          }
          // console.log('Saving', selectedSplit[0].get('id'));
          saveSurface(selectedSplit, null, changed);
        };

        if (debug < 16) console.log('Define onclick to save new feature');
        doneButton.onclick = (e) => {
          if (debug < 13) console.log('Clicked save new feature');
          assignSelectedDate(true);
          // console.log(original, changed);
          // e.target.disabled = true;
          const ff = selectClick.getFeatures().getArray();
          // console.log('outside', ff[0].get('verifiseringsdato'));
          const info = document.getElementById('informasjon');
          if (locallyAr5 && info.value && changed.informasjon === undefined) {
            // console.log('opphev felt og foreslå å tømme');
            info.style.border = 'thick solid red';
            info.style.transform = 'translate(50%) scale(2)';
            askToConfirm(
              'Skal du ikke slette informasjonen på flaten?'
            ).then( confirmed => {
              // console.log('svar er', confirmed ? 'ja' : 'nei');
              if (confirmed) // globalSelectedFeature.set('informasjon', '');
                selectClick.getFeatures().forEach(
                  f => f.set('informasjon', '')
                );
              confirmChanges(original, changed);
              // if ( noEmptyAttribute() ) { saveAllSplitFeatures(ff); }
              // bringUserBackToStart();
            });
          } else { // no question
            if (locallyAr5) confirmChanges(original, changed);
            else {
              saveAllSplitFeatures(ff);
              bringUserBackToStart();
            }
          }
        };

        const place = document.getElementById('info-box-text-no-action-text');
        const paht = 'pick-area-help-text' + (locallyAr5 ? '-ar5' : '');
        place.innerHTML = i18n[CURRENT_GEOTYPE][paht].join('\n');
        if (!locallyAr5 && CURRENT_GEOTYPE === 'line') {
          const b = document.createElement('button');
          b.className = 'btn green darken-2 small-button';
          b.innerText = 'Ferdig';
          b.onclick = () => {
            bringUserBackToStart();
            showToolbar('none');
          };
          place.appendChild(document.createElement('br'));
          place.appendChild(b);
        }

        if (CURRENT_GEOTYPE !== 'point') showInfoBoxNoAction();

        // Move tab one to the right
        switchTab('DrawPick', 'Save');

        if (CURRENT_GEOTYPE === 'point') {
          updatedVectorLayer.setStyle(invisibleStylePoint);
          // Open attribute editing of this object
          editFeatureAttributes(updatedVectorLayer.getSource().getFeatures()[0]);
        } else if (CURRENT_GEOTYPE === 'line') {
          updatedVectorLayer.setStyle(blueLineStyle);
        } else { // polygon
          updatedVectorLayer.setStyle(bluePolygonStyle);
        }

        // Setup done box if there is not empty attribute
        if (noEmptyAttribute()) {
          if (locallyAr5) nyAr5FlateMedUendretEgenskaper = true;
          regretButton.style.display = 'inline-block';
          if (CURRENT_GEOTYPE === 'point') {
            moveTabToOperations();
            simpleToast('Dine endringer er lagret!');
          }
        }
      }
    },
    error: handleSessionTimeout,
    complete: (c) => { // console.log('Completed! (Stop loading animation)');
      document.getElementById('bar').style.display = 'none';
      enableEditButtons(); // white again
      if (locallyAr5) {
        document.getElementById('twdStep3').hidden = true;
        document.getElementById('twdStep1').hidden = false;
        if (!c.responseJSON) { // error 400
          console.log(c.responseText?.slice(490) || c);
          // Keep drawing on rejected geometry:
          rehabilitateDrawing();
          showInfoBoxText();
        }
        else if (c.responseJSON.status !== 1) { // error
          // ferdigBtn.classList.add('hidden');
          // deleteLastDrawnPointBtn.classList.add('hidden');
        } else { // === 1 , it went well
          // console.log(OPERATION); // === 'add', coming from drawing
          showToolbar('edit'); // to ask to select feature
          setOperation('edit_new'); // it has to be after showToolbar('edit')
        }
      }
    },
    contentType: 'application/json',
    dataType: 'json'
  });
}

export function saveSurface(selectedFeatures, borderId, change) {
  if (debug < 16) console.log('Save to Postgis', selectedFeatures);

  const updateRequest = {
    layer_id: currentLayerId,
    enableSurfaceWithAttrReqSurfaceListEntities: []
  };
  if (!locallyAr5) { // reindrift
    const f = selectedFeatures[0]; // back-end does dot support multiple features
    const p = f.getProperties();
    if (change) Object.keys(p).forEach(k => {
      if (!change.hasOwnProperty(k)) delete p[k];
    }); else delete p.geometry; // send all attributes in case of new geometry
    updateRequest.feature_id = f.get('id');
    updateRequest.feature_properties = JSON.stringify(p);
  }
  const api = locallyAr5 ? '/enable_surfacelayer_request' : '/update_attributes';

  if ((debug < 12) && (selectedFeatures.length > 1)) {
    console.log(
      `More than one feature. If you do not want to save multiple features at
      the same time, you probably want to use globalSelectedFeature
      instead of randomly choose the first one`
    );
    // selectedFeatures.splice(1); // keep the first element
  }
  // Loop through all new objects:
  selectedFeatures.forEach(feature => {
    const surface = {};
    const id = feature.get('id');
    const map_update = { properties: { id } };

    surface['surface_opr'] = 'ENABLE';
    // surface['enableSurfaceWithAttrReqAttrValuesIntEntities'] = new Array();

    const prop = feature.getProperties();
    // If ar5, manually add kvalitet (80, 1, 0):
    if (locallyAr5) {
      if (debug < 13) console.log(prop);
      const defaultAr5 = { noyaktighet: 1, synbarhet: 0 };
      ['noyaktighet', 'synbarhet'].forEach(v => prop[v] = defaultAr5[v]);
    }

    // Loop through properties and add to json:
    updSurfaceWithPolyResAttrEntities.forEach(attribute => {
      const name = attribute['column_id'];
      if (name.indexOf('[') > -1) { // the value comes from an array element
        const s = name.split('[');
        arrayName = s[0];
        arrayIndex = s[1][0];
        // map_update['properties'][name] = prop[arrayName][arrayIndex];
        map_update['properties'][arrayName] = prop[arrayName];
      } else {
        map_update['properties'][name] = prop[name];
      }
      if (debug < 11) console.log(id, name, prop[name]);
    });

    const jsonS = JSON.stringify(map_update).replace(/"null"/g, "null");
    surface['map_update'] = '"' + jsonS + '"';
    updateRequest['enableSurfaceWithAttrReqSurfaceListEntities'].push(surface);
  });

  // Give visual feedback to user without waiting for server, i.e.
  // update vectorLayer's features, such that
  // user can see new changes while waiting for backend:
  const vls = vectorLayer.getSource();
  selectedFeatures.forEach(sf => {
    // If it exists, update its properties. Or, more easily, remove and add:
    if (vls.hasFeature(sf)) vls.removeFeature(sf);
    vls.addFeature(sf);
  });

  // Save features:
  // Cancel old saving when new values are going to be saved:
  if (XHRsaving) XHRsaving.abort();
  // An existing feature has been modified, send it to back-end:
  XHRsaving = $.ajax({
    type: 'POST',
    url: rest + api + token,
    data: JSON.stringify(updateRequest),
    success: (data) => {
      if (debug < 11) console.log('Response to modification request');
      if (data.status === 1) { // det gikk bra
        toastDestroy();
        if (borderId) removeBorder(borderId); else showToolbar('none');
        // if (locallyAr5) showMessageBox('updateMessage');
        if (!locallyAr5) simpleToast('Dine endringer er lagret!');
        // Check if there is any features that has empty attributes
        if (debug < 10) console.log('Show save-button or not?');
        if (noEmptyAttribute()) showDoneEditingBox();
      } else notifyAccordingToResponseStatus(data.status);
      // Reload geometry from server:
      setCurrentDataExtent([]); // force reload by skiping almostSameExtent()
      updateLayerData();
    },
    error: handleSessionTimeout,
    contentType: 'application/json',
    dataType: 'json'
  });
}

export function saveMovedPoint(selectedFeature) {
  if (debug < 16) console.log('saveMovedPoint');

  const epsg = map.getView().getProjection().getCode().split(':')[1];
  const updateRequest = {
    feature_properties: geojson.writeFeatureObject(selectedFeature),
    layer_id: currentLayerId,
    move_point_id: selectedFeature.get('id'),
    response_srid: epsg
  };
  updateRequest.feature_properties.geometry.crs = {
    type: 'name', properties: { name: `EPSG:${epsg}` }
  };
  updateRequest.feature_properties = JSON.stringify(updateRequest.feature_properties);

  fetch(rest + '/move' + token, {
    method: 'post',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(updateRequest)
  }).then(response => {
    if (response.ok) return response.json();
    else  {
      response.getResponseHeader = (h) => response.headers.get(h);
      handleSessionTimeout(response);
    }
  }).then(data => {
    // Removed drawn features
    drawnSource.clear();
    // Redraw base layer
    updateLayerData();
    toastDestroy();
    // Check for error
    if (!data || data.status !== 1) {
      // console.log('Failed to moved point in layer ' + currentLayerId);
      const objektet = i18n[CURRENT_GEOTYPE].objektet;
      const msg = 'Klarte ikke slette/flytte. Prøv på nytt og/eller sjekk' +
        ' om du har rettigheter for å endre dette ' + objektet + '.';
      materializeToast(msg, 'error');
    } else {
      // Update collection with moved point
      setCurrentDataExtent([]); // force reload by skiping almostSameExtent()
      updateLayerData();
      // bringUserBackToStart();
      simpleToast('Dine endringer er lagret!');
    }
  }).catch(e => console.log(e)); // .finally(() => console.log('finally'));
}

export const reprojectD3 = (oldProj, newProj) => {
  // Reproject the entire SVG layer to the new UTM zone,
  // while waiting new data from form the server, already in the right utm zone
  const transform = reProject(oldProj, newProj);
  setD3Path(d3.geoPath().projection(transform));
};

let updatedProj = false;
const updateProjection = () => {
  const v = map.getView();
  const previousProj = v.getProjection().getCode();
  const previousCenter = v.getCenter();
  let previousZoom = v.getZoom();

  const lonlat = projTransform(previousCenter, previousProj, 'EPSG:4326');
  const E = lonlat[0];
  const N = lonlat[1];

  // Zone selection, rough method
  const tolerance = 0.2; // degrees. Avoid switching too often
  // IF Nord < 65°: UTM32 (egentlig 64°)
  // IF Øst > 22°: UTM35 (egentlig 24°)
  // ELSE: UTM33
  const utm = getUtm();
  const barelyInside32 = (utm === 33) && ((65 - N) < tolerance);
  const barelyInside33N = (utm === 32) && ((N - 65) < tolerance);
  const barelyInside33E = (utm === 35) && ((22 - E) < tolerance);
  const barelyInside35 = (utm === 33) && ((E - 22) < tolerance);
  let newUtm = utm;
  if (N < 65) { // UTM32
    if (utm === 32 || barelyInside32) return;
    newUtm = 32;
  } else if (E > 22) { // UTM35
    if (utm === 35 || barelyInside35) return;
    newUtm = 35;
  } else { // UTM33
    if (utm === 33 || barelyInside33N || barelyInside33E) return;
    newUtm = 33;
  }

  const newProj = `EPSG:258${newUtm}`;
  const newCenter = projTransform(previousCenter, previousProj, newProj);

  // zoom for utm 35 seems one level deeper, visual correction here
  if (previousProj === 'EPSG:25835') previousZoom += 1;
  if (newProj === 'EPSG:25835') previousZoom -= 1;

  const newView = new View({
    center: newCenter,
    constrainOnlyCenter: true,
    extent: projGet(newProj).getExtent(),
    maxZoom: maxZoom,
    minZoom: minZoom,
    projection: newProj,
    zoom: previousZoom
  });

  const currentBasemap = document.getElementsByClassName('bg-highlight')[0];
  const baseType = currentBasemap ? currentBasemap.id : 'graatoner';
  const bmIndex = map.get('groupPosition') - 1; // before WMS
  map.getLayers().setAt(bmIndex, basemap[baseType][newProj]);

  map.setView(newView);
  link.update('utm', newUtm);
  updateVectorLayersProjection(previousProj, newProj);

  reprojectD3(currentDataProj, newProj);

  // console.log('Projection has been set to ', newProj);
  updatedProj = true;
  // document.getElementById('toolbar_help').innerText = newProj.slice(-2);
};

export function reProject(pSource, pDest) {
  if (pSource === 'EPSG:25832') {
    if (pDest === 'EPSG:25833') return d3.geoTransform({ point: proj32to33 });
    if (pDest === 'EPSG:25835') return d3.geoTransform({ point: proj32to35 });
  }
  if (pSource === 'EPSG:25833') {
    if (pDest === 'EPSG:25832') return d3.geoTransform({ point: proj33to32 });
    if (pDest === 'EPSG:25835') return d3.geoTransform({ point: proj33to35 });
  }
  if (pSource === 'EPSG:25835') {
    if (pDest === 'EPSG:25832') return d3.geoTransform({ point: proj35to32 });
    if (pDest === 'EPSG:25833') return d3.geoTransform({ point: proj35to33 });
  }
}

function proj32to33(x, y) {
  const point = projTransform([x, y], 'EPSG:25832', 'EPSG:25833');
  const pixel = map.getPixelFromCoordinate(point);
  this.stream.point(pixel[0], pixel[1]);
}

function proj32to35(x, y) {
  const point = projTransform([x, y], 'EPSG:25832', 'EPSG:25835');
  const pixel = map.getPixelFromCoordinate(point);
  this.stream.point(pixel[0], pixel[1]);
}

function proj33to32(x, y) {
  const point = projTransform([x, y], 'EPSG:25833', 'EPSG:25832');
  const pixel = map.getPixelFromCoordinate(point);
  this.stream.point(pixel[0], pixel[1]);
}

function proj33to35(x, y) {
  const point = projTransform([x, y], 'EPSG:25833', 'EPSG:25835');
  const pixel = map.getPixelFromCoordinate(point);
  this.stream.point(pixel[0], pixel[1]);
}

function proj35to32(x, y) {
  const point = projTransform([x, y], 'EPSG:25835', 'EPSG:25832');
  const pixel = map.getPixelFromCoordinate(point);
  this.stream.point(pixel[0], pixel[1]);
}

function proj35to33(x, y) {
  const point = projTransform([x, y], 'EPSG:25835', 'EPSG:25833');
  const pixel = map.getPixelFromCoordinate(point);
  this.stream.point(pixel[0], pixel[1]);
}

export const updateProjAndExtent = () => {
  updateProjection();
  if (getActiveTab() === 1) { updatedProj = false; return; } // no need to fetch data
  if (updatedProj) { updatedProj = false; return; } // avoid calling updateLayerData twice on moveend
  if (debug < 12) console.log('updateLayerData');
  updateLayerData();
};

function projFromPoint(lonlat) {
  let projection = 'EPSG:25833';
  const E = lonlat[0];
  const N = lonlat[1];
  // Zone selection, rough method:
  // IF Nord < 65°: UTM32
  // IF Øst > 22°: UTM35
  // ELSE: UTM33
  if (N < 65) projection = 'EPSG:25832';
  else if (E > 22) projection = 'EPSG:25835';
  else projection = 'EPSG:25833';
  return projection;
}

function adaptExtent(ext) {
  var minSize = 500; // metres
  var lx = Math.abs(ext[2] - ext[0]);
  var ly = Math.abs(ext[3] - ext[1]);

  if (lx < minSize) {
    ext[0] -= (minSize - lx) / 2;
    ext[2] += (minSize - lx) / 2;
  }
  if (ly < minSize) {
    ext[1] -= (minSize - ly) / 2;
    ext[3] += (minSize - ly) / 2;
  }
}

function updateAttributeValue(element, arrayIndex, selectedFeatures) {
  const id = element.id;
  const value = element.value;
  // console.log('Saving single value...', id, value);
  // if (value === '') { value = null; } // otherwise it will not be saved
  // but if I set it to null in text fields, it complains about missing value
  if (id.indexOf('reinbeitebruker_id2') > -1) {
    const infoBox = element.closest('.info-box');
    const sibling3 = infoBox.getElementsByClassName('reinbeitebruker_id3')[0];
    if (value) { // show reinbeitebruker_id3 field
      sibling3.parentNode.style.display = 'block';
    } else { // hide reinbeitebruker_id3 field
      if (!sibling3.value) {
        sibling3.parentNode.style.display = 'none';
      } else { // transfer value from third field to second field
        element.value = sibling3.value;
        sibling3.value = '';
        // disable option in third field, since it is now selected in second field
        const i = getIndexFromValue(value, sibling3);
        // console.log(i, e.value);
        // The following does not work it is overwritten by another event
        if (i > 0) { sibling3.options[i].disable = true; }
      }
    }
  }
  selectedFeatures.forEach((olf, i) => {
    const object = {};
    if (arrayIndex < 0) {
      object[id] = value;
    } else {
      const arr = olf.getProperties()[arrayName];
      arr[arrayIndex] = parseInt(value, 10);
      object[arrayName] = arr;
    }
    olf.setProperties(object);
    // If user is currently changing the parameter which controls the style,
    // then change the style on the map.
    // We also have to change the state for when objects are deleted or enabled:
    const ls = layerStyle;
    if (ls.styleAttribute === id || ls.styleAttributeDelete === id) {
      // console.log('Update attribute:', id);
      // Change topojson and update svg layer:
      const idOl = olf.getProperties().id;
      collection.features.forEach(feature => {
        if (feature.properties.id === idOl) {
          // Set new value
          if (arrayIndex < 0) {
            feature.properties[id] = value;
          } else {
            feature.properties[arrayName][arrayIndex] = value;
          }
          updateSVG();
        }
      });
    }
  });
}
